import z from 'zod';

import taxonomy from './experience_taxonomy.json';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint } from '../common';

export enum ListingClassificationClassifier {
  TEXT_BASED = 'text-based',
  LLM_BASED = 'llm-based',
  CBSA = 'cbsa',
  EUROSTAT = 'eurostat',
  OECD = 'oecd',
}

export enum ListingClassificationDomain {
  HOLIDAY = 'holiday',
  MARKET = 'market',
  SEGMENT = 'segment',
  CATEGORY = 'category',
  WELLBEING_MODALITY = 'wellbeing_modality',
  ACTIVITY_TYPE = 'activity_type',
  RENTAL_ASSET_TYPE = 'rental_asset_type',
  SERVICE_TYPE = 'service_type',
  AGE_POLICY = 'age_policy',
  PHYSICAL_INTENSITY = 'physical_intensity',
  SEASONALITY = 'seasonality',
  WEATHER_DEPENDENCY = 'weather_dependency',
  RISK = 'risk',
}

export const ListingClassificationSchema = z.object({
  classifier: z.nativeEnum(ListingClassificationClassifier),
  createdAt: z.string().datetime(),
  domain: z.nativeEnum(ListingClassificationDomain),
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  updatedAt: z.string().datetime(),
  value: z.string(),
});

export type ListingClassification = z.infer<typeof ListingClassificationSchema>;

export const GetListingClassificationsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId }: { listingId: string }) => `/v2/listing-classification/${listingId}`,
  requestSchema: z.void(),
  responseSchema: z.array(ListingClassificationSchema),
});

export const BackfillListingClassificationRequestSchema = z.object({
  brandId: z.string().optional(),
  classifiersToRun: z.array(z.nativeEnum(ListingClassificationClassifier)).optional(),
  domainsToRun: z.array(z.nativeEnum(ListingClassificationDomain)).optional(),
});

export const BackfillListingClassificationResponseSchema = z.object({
  listingsQueued: z.number(),
});

export const BackFillListingClassificationEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `/v2/listing-classification/backfill`,
  requestSchema: BackfillListingClassificationRequestSchema,
  responseSchema: BackfillListingClassificationResponseSchema,
});

// ---------------------------------------------------------------------------
// Listing Classification Label CRUD
// ---------------------------------------------------------------------------

export const ListingClassificationLabelSchema = z.object({
  id: z.string().uuid(),
  domain: z.nativeEnum(ListingClassificationDomain),
  values: z.array(z.string()),
  label: z.string(),
  description: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type ListingClassificationLabel = z.infer<typeof ListingClassificationLabelSchema>;

export const CreateListingClassificationLabelRequestSchema = z.object({
  domain: z.nativeEnum(ListingClassificationDomain),
  values: z.array(z.string()).nonempty(),
  label: z.string().max(255),
  description: z.string().max(1000).nullable().optional(),
});

export const UpdateListingClassificationLabelRequestSchema = z.object({
  domain: z.nativeEnum(ListingClassificationDomain).optional(),
  values: z.array(z.string()).nonempty().optional(),
  label: z.string().max(255).optional(),
  description: z.string().max(1000).nullable().optional(),
});

export const ListListingClassificationLabelsQuerySchema = z.object({
  domain: z.nativeEnum(ListingClassificationDomain).optional(),
  value: z.string().optional(),
  values: z.array(z.string()).optional(),
});

export const ListListingClassificationLabelsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof ListListingClassificationLabelsQuerySchema> }) =>
    `/v2/listing-classification-labels/?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: z.array(ListingClassificationLabelSchema),
  querySchema: ListListingClassificationLabelsQuerySchema,
});

export const GetListingClassificationLabelEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ id }: { id: string }) => `/v2/listing-classification-labels/${id}`,
  requestSchema: z.void(),
  responseSchema: ListingClassificationLabelSchema,
});

export const CreateListingClassificationLabelEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `/v2/listing-classification-labels/`,
  requestSchema: CreateListingClassificationLabelRequestSchema,
  responseSchema: ListingClassificationLabelSchema,
});

export const UpdateListingClassificationLabelEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ id }: { id: string }) => `/v2/listing-classification-labels/${id}`,
  requestSchema: UpdateListingClassificationLabelRequestSchema,
  responseSchema: ListingClassificationLabelSchema,
});

export const DeleteListingClassificationLabelEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ id }: { id: string }) => `/v2/listing-classification-labels/${id}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export { taxonomy as EXPERIENCE_TAXONOMY };

export const TAXONOMY_DOMAINS = Object.entries(taxonomy.domains)
  .map(([domain, data]) => ({
    domain,
    label: data.label,
    options: data.values.map((v) => ({ value: v.key, label: v.name })).sort((a, b) => a.label.localeCompare(b.label)),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));
